import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ChevronLeft, Loader2, ListChecks, Wrench, BookOpen, Check, ExternalLink,
  AlertTriangle, Clock, DollarSign, Star,
} from 'lucide-react';
import { academyActionCenter, academyActionToggle } from '../lib/api';

const PRIORITY_LABEL = {
  critical: { color: '#ff4757', label: 'Critical' },
  high:     { color: '#ffcf52', label: 'High' },
  medium:   { color: '#22d3ee', label: 'Medium' },
  low:      { color: '#94a3b8', label: 'Low' },
};

const TOOL_KIND = {
  template:   { color: '#a78bfa', label: 'Template' },
  service:    { color: '#34d399', label: 'Service' },
  lookup:     { color: '#22d3ee', label: 'Lookup' },
  calculator: { color: '#ffcf52', label: 'Calculator' },
  guide:      { color: '#f472b6', label: 'Guide' },
  tool:       { color: '#a78bfa', label: 'Tool' },
};

export default function AcademyActionCenter() {
  const { trackId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const load = async () => {
    setLoading(true);
    try { setData(await academyActionCenter(trackId)); }
    catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load action center');
      setData(null);
    }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [trackId]);

  const toggle = async (item) => {
    setBusy(b => ({ ...b, [item.id]: true }));
    try {
      const next = !item.done;
      await academyActionToggle(trackId, item.id, next);
      setData(d => ({
        ...d,
        checklist: d.checklist.map(i => i.id === item.id ? { ...i, done: next } : i),
        completed: d.completed + (next ? 1 : -1),
      }));
    } catch { toast.error('Could not update'); }
    setBusy(b => ({ ...b, [item.id]: false }));
  };

  if (loading) return <div className="flex items-center gap-2 text-white/60 py-20"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>;
  if (!data) return <div className="text-center py-16 text-white/60">Action center not found. <Link to="/academy" className="text-[#ffcf52] underline">Back to Academy</Link></div>;

  const pct = data.total > 0 ? Math.round((data.completed / data.total) * 100) : 0;

  return (
    <div className="max-w-[1100px] mx-auto" data-testid="action-center-page">
      <Link to="/academy" className="inline-flex items-center gap-1 text-[12.5px] text-white/55 hover:text-white mb-3" data-testid="action-center-back"><ChevronLeft className="w-3.5 h-3.5" /> Back to Academy</Link>

      <header className="mb-6">
        <div className="chip" style={{ color: data.color, borderColor: data.color + '55' }}><ListChecks className="w-3 h-3" /> Action Center</div>
        <h1 className="font-display text-[32px] sm:text-[42px] md:text-[52px] leading-tight mt-2">{data.title.replace(' · Action Center', '')}</h1>
        <p className="text-white/65 text-[14px] md:text-[15.5px] mt-2 max-w-[680px]">{data.tagline}</p>

        <div className="mt-5 max-w-[480px]">
          <div className="flex items-center justify-between text-[11.5px] text-white/65 mb-1.5">
            <span>{data.completed} of {data.total} steps complete</span>
            <span style={{ color: data.color }}>{pct}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-white/[0.07] overflow-hidden">
            <div className="h-full rounded-full transition-all" style={{ width: pct + '%', background: data.color }} />
          </div>
        </div>
      </header>

      <section className="mb-7" data-testid="action-center-checklist">
        <div className="flex items-center gap-2 mb-3">
          <ListChecks className="w-4 h-4 text-white/55" />
          <h2 className="row-title text-white">Action checklist</h2>
        </div>
        <ul className="grid gap-2">
          {data.checklist.map(item => {
            const pri = PRIORITY_LABEL[item.priority] || PRIORITY_LABEL.medium;
            return (
              <li key={item.id} className={`card-shell p-4 transition-all ${item.done ? 'opacity-65' : ''}`} data-testid={`action-item-${item.id}`}>
                <div className="flex items-start gap-3">
                  <button
                    onClick={() => toggle(item)}
                    disabled={busy[item.id]}
                    className="mt-0.5 w-6 h-6 shrink-0 rounded-md border-2 grid place-items-center transition-all"
                    style={{ borderColor: item.done ? data.color : 'rgba(255,255,255,0.3)', background: item.done ? data.color : 'transparent' }}
                    aria-label={item.done ? 'Mark incomplete' : 'Mark complete'}
                    data-testid={`action-toggle-${item.id}`}
                  >
                    {busy[item.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : item.done && <Check className="w-3.5 h-3.5 text-black" strokeWidth={3} />}
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5 mb-1">
                      <span className="chip text-[10px]" style={{ color: pri.color, borderColor: pri.color + '55' }}>
                        {item.priority === 'critical' && <AlertTriangle className="w-2.5 h-2.5" />}
                        {pri.label}
                      </span>
                      {item.effort && <span className="chip text-[10px] text-white/55"><Clock className="w-2.5 h-2.5" /> {item.effort}</span>}
                      {item.cost && <span className="chip text-[10px] text-white/55"><DollarSign className="w-2.5 h-2.5" /> {item.cost}</span>}
                    </div>
                    <div className={`font-semibold text-[14px] ${item.done ? 'line-through' : ''}`}>{item.label}</div>
                    {item.why && <p className="text-[12.5px] text-white/60 mt-1 leading-relaxed">{item.why}</p>}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      {data.tools?.length > 0 && (
        <section className="mb-7" data-testid="action-center-tools">
          <div className="flex items-center gap-2 mb-3">
            <Wrench className="w-4 h-4 text-white/55" />
            <h2 className="row-title text-white">Tools &amp; templates</h2>
          </div>
          <ul className="grid sm:grid-cols-2 gap-2">
            {data.tools.map(t => {
              const k = TOOL_KIND[t.kind] || TOOL_KIND.tool;
              const isInternal = t.href?.startsWith('/');
              return (
                <li key={t.id}>
                  {isInternal ? (
                    <Link to={t.href} className="card-shell p-4 hover-lift block h-full" data-testid={`tool-${t.id}`}>
                      <ToolBody tool={t} kind={k} />
                    </Link>
                  ) : (
                    <a href={t.href} target="_blank" rel="noreferrer noopener" className="card-shell p-4 hover-lift block h-full" data-testid={`tool-${t.id}`}>
                      <ToolBody tool={t} kind={k} external />
                    </a>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {data.deeper_reads?.length > 0 && (
        <section className="mb-12" data-testid="action-center-reads">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen className="w-4 h-4 text-white/55" />
            <h2 className="row-title text-white">Deeper reads</h2>
          </div>
          <ul className="grid gap-2">
            {data.deeper_reads.map(r => (
              <li key={r.id}>
                <a href={r.href} target="_blank" rel="noreferrer noopener" className="card-shell p-3.5 hover-lift block flex items-start gap-3" data-testid={`read-${r.id}`}>
                  <Star className="w-4 h-4 mt-0.5 shrink-0" style={{ color: data.color }} />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[13.5px] truncate">{r.title}</div>
                    <div className="text-[11.5px] text-white/55 mt-0.5">{r.source}</div>
                  </div>
                  <ExternalLink className="w-3.5 h-3.5 text-white/35 shrink-0" />
                </a>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function ToolBody({ tool, kind, external }) {
  return (
    <>
      <div className="flex items-center justify-between mb-2">
        <span className="chip text-[10px]" style={{ color: kind.color, borderColor: kind.color + '55' }}>{kind.label}</span>
        {external && <ExternalLink className="w-3.5 h-3.5 text-white/35" />}
      </div>
      <div className="font-semibold text-[13.5px]">{tool.label}</div>
      <p className="text-[12px] text-white/60 mt-1 leading-relaxed">{tool.desc}</p>
    </>
  );
}
